import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CrmAuth, CrmLead, PollAnswer } from './crmApi';
import { StartWorkCard } from './Attendance';
import { HolidayBanner } from './Holidays';
import LeadPanel, { PollButtons } from './LeadPanel';
import LogCallModal from './LogCallModal';
import { MeetingCard } from './Meetings';
import PollModal from './PollModal';
import { Empty, LeadRow, PageHead, ShiftChip, Spinner, fmtHm, fmtWhen, goldBg, inputCls, useCrmMe } from './ui';
import { greeting } from './viz';

// ── The salesperson's call pages: Today, My Leads, Follow-ups ────────────
// (A Lead Manager or Sales Admin who takes calls gets them too.)

const Section: React.FC<{ icon: string; title: string; sub?: string; tone?: string; count: number; children: React.ReactNode }> = ({ icon, title, sub, tone = 'text-gray-800', count, children }) => (
  <div className="space-y-2" role="region" aria-label={title}>
    <div className="px-1">
      <h2 className={`text-sm font-bold ${tone}`}><span aria-hidden>{icon}</span> {title} <span className="text-gray-400 font-semibold">· {count}</span></h2>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
    {children}
  </div>
);

const Stat: React.FC<{ n: number; label: string }> = ({ n, label }) => (
  <div className="rounded-xl bg-white/10 px-1.5 py-2 text-center min-w-0">
    <p className="text-xl font-bold leading-none">{n}</p>
    <p className="text-[10px] text-white/70 mt-1 leading-tight">{label}</p>
  </div>
);

type TodayData = Awaited<ReturnType<typeof crm.today>>;
const SEPARATE = ['Poll Sent', 'Interested'];

// Today: everything to do, most urgent first. The poll's status buttons and
// "They paid" sit right on the rows.
export const CrmToday: React.FC<{ auth: CrmAuth; name?: string; onNavigate?: (page: string) => void; embedded?: boolean }> = ({ auth, name, onNavigate, embedded }) => {
  const { me, error } = useCrmMe(auth);
  const [data, setData] = useState<TodayData | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [poll, setPoll] = useState<{ lead: CrmLead; answer: PollAnswer } | null>(null);
  const [paid, setPaid] = useState<CrmLead | null>(null);

  const load = useCallback(async () => {
    try { setData(await crm.today(auth)); } catch (e: any) { toast.error(e.message); }
  }, [auth]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 60000);
    return () => clearInterval(t);
  }, [load]);

  if (error) return <Empty icon="⚠️" title="Could not open the CRM" sub={error} />;
  if (!data || !me) return <Spinner label="Loading today…" />;
  const { counts } = data;
  const polls = [...data.polls].sort((a, b) => Number(b.pollDue) - Number(a.pollDue));
  const callNow = data.callNow.filter(l => !SEPARATE.includes(l.stage));
  const later = data.later.filter(l => !SEPARATE.includes(l.stage));
  const meetings = data.meetings || [];
  const nothing = !polls.length && !callNow.length && !data.fresh.length && !data.payments.length && !later.length && !meetings.length;
  const shift = data.shift?.thisWeek || null;
  const first = (name || '').split(' ')[0];
  const done = () => { setPoll(null); setPaid(null); load(); };

  return (
    <div className="space-y-5">
      {!embedded ? (
        <div className="rounded-3xl p-5 text-white" style={{ background: 'linear-gradient(135deg,#1a1428 0%,#2b2240 55%,#4e3a69 100%)' }}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <p className="text-white/60 text-sm">{greeting()}{first ? `, ${first}` : ''} 👋</p>
              <h1 className="text-2xl font-bold mt-0.5">Today</h1>
              {data.shift && <div className="mt-2"><ShiftChip shift={shift} onNow={data.shift.onNow} dark /></div>}
            </div>
            <div className="flex gap-2 flex-wrap">
              {onNavigate && <button onClick={() => onNavigate('requests')} className="px-4 py-2.5 rounded-xl text-sm font-bold text-white" style={goldBg}>＋ Add a lead</button>}
              <button onClick={load} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-white/20 text-white/80 hover:bg-white/10">↻ Refresh</button>
            </div>
          </div>
          {/* Counted as the EOD report counts them; brochures: the Monitor's box (poll still to go out) */}
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mt-4">
            <Stat n={counts.calls} label="calls" />
            <Stat n={counts.callLater ?? 0} label="call later" />
            <Stat n={counts.cold ?? 0} label="cold" />
            <Stat n={counts.pitched} label="brochures" />
            <Stat n={counts.visits ?? 0} label="direct visits" />
            <Stat n={counts.pollsSent} label="polls sent" />
            <Stat n={counts.interestedToday ?? 0} label="interested" />
            <Stat n={counts.converted} label="converted" />
          </div>
        </div>
      ) : (
        <PageHead title="Today" sub={`${counts.calls} calls · ${counts.callLater ?? 0} call later · ${counts.cold ?? 0} cold · ${counts.pitched} brochures · ${counts.visits ?? 0} direct visits · ${counts.pollsSent} polls · ${counts.interestedToday ?? 0} interested · ${counts.converted} converted`}
          right={<button onClick={load} className="px-3 py-2 rounded-xl text-xs font-semibold border border-white/20 text-white/80 hover:bg-white/10">↻ Refresh</button>} />
      )}

      {/* Start work / End work at the office (a Salesperson, with attendance on) */}
      <StartWorkCard auth={auth} />
      <HolidayBanner note={data.holiday} />
      {!data.working && !data.holiday?.today && <p className="text-xs text-gray-500 bg-white rounded-xl border border-gray-100 px-4 py-3">Outside working hours. New leads go out at the start of the next working day.</p>}
      {data.working && data.shift && shift && !data.shift.onNow && (
        <p className="text-xs text-indigo-800 bg-indigo-50 rounded-xl border border-indigo-100 px-4 py-3">Your shift is {fmtHm(shift.start)} – {fmtHm(shift.end)}. New leads come to you during it.</p>
      )}

      {nothing ? (
        <Empty icon="🎉" title="Nothing to do right now" sub="Polls to update, callbacks, new leads and payments show up here." />
      ) : (
        <>
          {meetings.length > 0 && (
            <Section icon="🤝" title="Coordinator meetings" tone={counts.linksToSend ? 'text-red-700' : 'text-indigo-800'} count={meetings.length}
              sub={counts.linksToSend ? `${counts.linksToSend} meeting link${counts.linksToSend === 1 ? '' : 's'} to send to the student` : 'Booked from your pitches'}>
              <div className="grid gap-2 lg:grid-cols-2">
                {meetings.map(m => <MeetingCard key={m.id} auth={auth} m={m} onChanged={load} onOpenLead={setOpen} />)}
              </div>
            </Section>
          )}
          {polls.length > 0 && (
            <Section icon="📊" title="Update the poll status" tone={counts.pollsDue ? 'text-red-700' : 'text-fuchsia-800'} count={polls.length}
              sub={counts.pollsDue ? `${counts.pollsDue} due now. Tap what they said.` : 'Due 3 hours after the poll went out. Tap what they said.'}>
              {polls.map(l => (
                <LeadRow key={l.id} lead={l} onOpen={() => setOpen(l.id)} maxOwners={data.maxOwners}
                  extra={l.pollSentAt ? <p className="text-[11px] text-gray-400 mt-0.5">Poll sent {fmtWhen(l.pollSentAt)}</p> : undefined}
                  actions={<PollButtons small onPick={a => setPoll({ lead: l, answer: a })} />} />
              ))}
            </Section>
          )}
          {callNow.length > 0 && (
            <Section icon="📞" title="Call now" tone="text-red-700" sub="Callbacks, retries and visits that are due" count={callNow.length}>
              {callNow.map(l => <LeadRow key={l.id} lead={l} onOpen={() => setOpen(l.id)} maxOwners={data.maxOwners} />)}
            </Section>
          )}
          {data.fresh.length > 0 && (
            <Section icon="🆕" title="New leads" sub="Talk to them today. Not picked, a lead goes to the next salesperson after your 3 tries or when your shift ends." count={data.fresh.length}>
              {data.fresh.map(l => <LeadRow key={l.id} lead={l} onOpen={() => setOpen(l.id)} maxOwners={data.maxOwners} />)}
            </Section>
          )}
          {data.payments.length > 0 && (
            <Section icon="💰" title="Waiting for payment" tone="text-green-800" sub="They said Interested. When they pay, mark them Converted." count={data.payments.length}>
              {data.payments.map(l => (
                <LeadRow key={l.id} lead={l} onOpen={() => setOpen(l.id)} maxOwners={data.maxOwners}
                  actions={<button onClick={() => setPaid(l)} className="px-3 py-1.5 rounded-xl text-[11px] font-bold text-white bg-green-600 hover:bg-green-700">💰 They paid: Converted</button>} />
              ))}
            </Section>
          )}
          {later.length > 0 && (
            <Section icon="🕒" title="Later today" count={later.length}>
              {later.map(l => <LeadRow key={l.id} lead={l} onOpen={() => setOpen(l.id)} maxOwners={data.maxOwners} />)}
            </Section>
          )}
        </>
      )}
      {open && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
      {poll && <PollModal auth={auth} lead={poll.lead} me={me} initial={poll.answer} onClose={() => setPoll(null)} onSaved={done} />}
      {paid && <LogCallModal auth={auth} lead={paid} me={me} initialOutcome="Converted" onClose={() => setPaid(null)} onSaved={done} />}
    </div>
  );
};

// The stage filters on My Leads: a few groups instead of every stage
const FILTERS: { id: string; label: string; stages: string[] | null }[] = [
  { id: 'open', label: 'Open', stages: ['New', 'Not Picked', 'Call Later', 'Meeting Booked', 'P2P', 'Poll Sent', 'Interested', 'No Response', 'Future Prospect', 'Direct Visit'] },
  { id: 'Meeting Booked', label: 'Meeting booked', stages: ['Meeting Booked'] },
  { id: 'P2P', label: 'P2P', stages: ['P2P'] },
  { id: 'tocall', label: 'To call', stages: ['New', 'Not Picked'] },
  { id: 'followup', label: 'Follow-ups', stages: ['Call Later', 'Future Prospect', 'Direct Visit'] },
  { id: 'Poll Sent', label: 'Poll sent', stages: ['Poll Sent'] },
  { id: 'Interested', label: 'Interested', stages: ['Interested'] },
  { id: 'No Response', label: 'No response', stages: ['No Response'] },
  { id: 'Converted', label: 'Converted', stages: ['Converted'] },
  { id: 'closed', label: 'Closed', stages: ['Cold Lead', 'Not Interested'] },
  { id: 'all', label: 'All', stages: null },
];
// Older links (My Week's "See all" Not Interested) open the group it is in
const FILTER_OF: Record<string, string> = { 'Not Interested': 'closed', 'Cold Lead': 'closed', New: 'tocall', 'Not Picked': 'tocall', 'Call Later': 'followup' };

export const CrmMyLeads: React.FC<{ auth: CrmAuth; initialStage?: string; embedded?: boolean }> = ({ auth, initialStage, embedded }) => {
  const { me, error } = useCrmMe(auth);
  const start = initialStage ? FILTER_OF[initialStage] || initialStage : 'open';
  const [stage, setStage] = useState<string>(FILTERS.some(f => f.id === start) ? start : 'open');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.myLeads>> | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 350); return () => clearTimeout(t); }, [q]);
  const load = useCallback(async () => {
    try { setData(await crm.myLeads(auth, stage, query)); } catch (e: any) { toast.error(e.message); }
  }, [auth, stage, query]);
  useEffect(() => { load(); }, [load]);

  const counts = data?.counts || {};
  const countOf = (f: typeof FILTERS[number]) => (f.stages ? f.stages.reduce((n, s) => n + (counts[s] || 0), 0) : Object.values(counts).reduce((a, b) => a + b, 0));
  if (error) return <Empty icon="⚠️" title="Could not open the CRM" sub={error} />;
  return (
    <div className="space-y-4">
      {!embedded && <PageHead title="My Leads" sub="Every lead you have. Search, or pick a group." />}
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, number, course…" className={`${inputCls} sm:max-w-sm`} aria-label="Search my leads" />
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1" role="tablist" aria-label="Group">
        {FILTERS.map(f => (
          <button key={f.id} role="tab" aria-selected={stage === f.id} onClick={() => setStage(f.id)} className="px-3 py-1.5 rounded-full text-xs font-bold border transition-all whitespace-nowrap"
            style={stage === f.id ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' } : { background: '#fff', color: '#4b5563', borderColor: '#e5e7eb' }}>
            {f.label} <span className="opacity-60">{countOf(f)}</span>
          </button>
        ))}
      </div>
      {!data || !me ? <Spinner /> : data.leads.length === 0 ? (
        <Empty icon="📭" title={query ? 'No leads match' : 'No leads here'} />
      ) : (
        <div className="space-y-2">{data.leads.map(l => <LeadRow key={l.id} lead={l} onOpen={() => setOpen(l.id)} maxOwners={data.maxOwners} />)}</div>
      )}
      {open && me && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};

const IST_DAY = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

export const CrmFollowUps: React.FC<{ auth: CrmAuth; embedded?: boolean }> = ({ auth, embedded }) => {
  const { me, error } = useCrmMe(auth);
  const [data, setData] = useState<Awaited<ReturnType<typeof crm.followUps>> | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setData(await crm.followUps(auth)); } catch (e: any) { toast.error(e.message); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    if (!data) return [];
    const now = Date.now();
    const tomorrow = IST_DAY(new Date(now + 86400000).toISOString());
    const out = new Map<string, CrmLead[]>();
    for (const l of data.leads) {
      const late = new Date(l.nextAt!).getTime() < now;
      const day = IST_DAY(l.nextAt!);
      const key = late ? 'Late' : day === data.today ? 'Today' : day === tomorrow ? 'Tomorrow'
        : new Date(l.nextAt!).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'long', day: 'numeric', month: 'short' });
      if (!out.has(key)) out.set(key, []);
      out.get(key)!.push(l);
    }
    return Array.from(out.entries());
  }, [data]);

  if (error) return <Empty icon="⚠️" title="Could not open the CRM" sub={error} />;
  if (!data || !me) return <Spinner label="Loading follow-ups…" />;
  return (
    <div className="space-y-5">
      {!embedded && <PageHead title="Follow-ups" sub="Everything with a date: callbacks, polls to update, payment dates, visits" />}
      {groups.length === 0 ? <Empty icon="🗓️" title="No follow-ups" sub="Call Later, Poll Sent, Interested, Future Prospect and Direct Visit dates show up here." /> : groups.map(([day, leads]) => (
        <div key={day} className="space-y-2">
          <h2 className={`text-sm font-bold uppercase tracking-wide px-1 ${day === 'Late' ? 'text-red-600' : 'text-gray-700'}`}>{day} <span className="text-gray-400">· {leads.length}</span></h2>
          {leads.map(l => <LeadRow key={l.id} lead={l} onOpen={() => setOpen(l.id)} maxOwners={3} />)}
        </div>
      ))}
      {open && <LeadPanel auth={auth} leadId={open} me={me} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
};
